import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'

import { ROLE_CAPABILITY_PREFIXES, RoleVisibility } from 'api/capabilities'
import { Capability, Role } from 'api/clients'

export const SENSITIVE_CAPABILITY_NOTES: Readonly<Record<string, string>> = Object.freeze({
  'admin:admin_capabilities:write': 'Holders can give admin roles to admins.',
  'admin:all:view': 'Admin sign-in requires this capability.',
  'global:admin:all': 'Every /batcave admin route requires this capability.',
  'global:role:write': 'Holders can change every role.',
})

export type CapabilityChanges = {
  added: Capability[]
  removed: Capability[]
}

export type RoleFields = {
  displayName: string
  description: string
  capabilities: Capability[]
}

export type RoleChanges = CapabilityChanges & {
  displayName: { before: string; after: string } | null
  descriptionChanged: boolean
}

export function isAdminCapability(capabilityName: string): boolean {
  return capabilityName.startsWith('admin:') || capabilityName.startsWith('global:')
}

export function roleCanHoldCapability(roleVisibility: RoleVisibility, capabilityName: string): boolean {
  switch (roleVisibility) {
    case RoleVisibility.ADMIN:
      return isAdminCapability(capabilityName)
    case RoleVisibility.PUBLIC:
      return !isAdminCapability(capabilityName)
    case RoleVisibility.PRIVATE:
      return true
  }
}

export function capabilityPrefixesForRole(roleVisibility: RoleVisibility): string[] {
  return ROLE_CAPABILITY_PREFIXES.filter((prefix) => roleCanHoldCapability(roleVisibility, `${prefix}:`))
}

export function groupCapabilitiesByPrefix(capabilities: Capability[]): [string, Capability[]][] {
  const groups = groupBy(sortBy(capabilities, 'name'), (capability) => capability.name.split(':')[0])
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
}

export function diffCapabilities(before: Capability[], after: Capability[]): CapabilityChanges {
  const beforeNames = new Set(before.map((capability) => capability.name))
  const afterNames = new Set(after.map((capability) => capability.name))

  return {
    added: after.filter((capability) => !beforeNames.has(capability.name)),
    removed: before.filter((capability) => !afterNames.has(capability.name)),
  }
}

export function roleFields(role: Pick<Role, 'displayName' | 'description' | 'capabilities'>): RoleFields {
  return {
    displayName: role.displayName || '',
    description: role.description || '',
    capabilities: role.capabilities,
  }
}

export function diffRole(before: RoleFields, after: RoleFields): RoleChanges {
  return {
    ...diffCapabilities(before.capabilities, after.capabilities),
    displayName:
      before.displayName === after.displayName ? null : { before: before.displayName, after: after.displayName },
    descriptionChanged: before.description !== after.description,
  }
}

export function hasRoleChanges(changes: RoleChanges): boolean {
  return (
    changes.added.length > 0 || changes.removed.length > 0 || changes.displayName !== null || changes.descriptionChanged
  )
}
